import _chunk from 'lodash/chunk';
import _get from 'lodash/get';
import _reduce from 'lodash/reduce';
import _last from 'lodash/last';
import _times from 'lodash/times';
import { forkJoin, of, interval } from 'rxjs';
import { mergeMap, delayWhen } from 'rxjs/operators';
import { deliveriesFromPropsSelector } from '../../selectors/new-booking-selectors';
import { ajaxFetchDeliveriesQuote } from '../../helper/anytime-booking';
import { isUnauthorizedError } from './common-errors';

const DEFAULT_CHUNK_SIZE = 500;
const DEFAULT_CHUNK_THROTTLE_IN_MS = 300;

const getQuotesFromAjaxResponse = response =>
  _get(response, 'response.quotes', []);
const getErrorFromAjaxResponse = response => _get(response, 'error', '');

/**
 * Returns deliveries quote with interception to handle missing quotes
 * @param state$
 * @param deliveries
 */
const fetchDeliveriesQuoteObservable = (state$, deliveries) =>
  ajaxFetchDeliveriesQuote(deliveries, state$.value).pipe(
    mergeMap(response => {
      const error = getErrorFromAjaxResponse(response);
      if (error) {
        // make up for the missing quotes with fake objects
        const fakeQuotes = _times(deliveries.length, () => ({
          _errorMessage: 'failed to get quote',
        }));
        return of({
          response: {
            quotes: [...fakeQuotes],
          },
          error,
        });
      }
      return of(response);
    }),
  );

/**
 * Retrieves delivery quotes by multiple chunks
 * @param state$
 * @param params
 */
export const getBatchQuoteUpdateByDeliveryIDsObservables = (state$, params) => {
  const { deliveryIDs, quoteRequestConfig } = params;
  const chunkSize = _get(quoteRequestConfig, 'chunkSize', DEFAULT_CHUNK_SIZE);
  const chunkThrottleTimeInMs = _get(
    quoteRequestConfig,
    'chunkThrottleTimeInMs',
    DEFAULT_CHUNK_THROTTLE_IN_MS,
  );

  const allDeliveries = deliveriesFromPropsSelector(state$.value, {
    deliveryIDs,
  });

  const operators = [];

  const chunks = _chunk(allDeliveries, chunkSize);
  chunks.forEach((deliveries, i) => {
    // accumulate each previous result and pass to the next operation
    const op = mergeMap(allPrevious => {
      const prevObs = allPrevious.map(r => of(r));
      const error = getErrorFromAjaxResponse(_last(allPrevious));

      // stop retrieval when encounter unauthorized
      if (isUnauthorizedError(error)) {
        return forkJoin(...prevObs);
      }

      return forkJoin(
        ...prevObs,
        fetchDeliveriesQuoteObservable(state$, deliveries),
      );
    });
    operators.push(op);

    // add delay except for last chunk
    if (i < chunks.length - 1) {
      operators.push(
        delayWhen(allPrevious => {
          const error = getErrorFromAjaxResponse(_last(allPrevious));

          if (isUnauthorizedError(error)) {
            return interval(0);
          }

          return interval(chunkThrottleTimeInMs);
        }),
      );
    }
  });

  // begin with empty array to store results
  return of([])
    .pipe(...operators)
    .pipe(
      mergeMap(results => {
        // accumulate all results into one response
        const accumulatedResponse = _reduce(
          results,
          (acc, r) => {
            const quotes = getQuotesFromAjaxResponse(r);
            const error = getErrorFromAjaxResponse(r);

            // stores last error
            if (error) {
              acc.error = error;
            }

            acc.response.quotes.push(...quotes);
            return acc;
          },
          {
            response: {
              quotes: [],
            },
            error: '',
          },
        );
        return forkJoin(of(deliveryIDs), of(accumulatedResponse));
      }),
    );
};

export default {};
